import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { months, years } from '@repo/toolkit/constants/calendar'
import { DateFormatter } from '@repo/toolkit/date'
import { useBreakPoint } from '@repo/toolkit/use-break-point'
import Box from '@repo/ui/box'
import Button from '@repo/ui/button'
import FullCalendar from '@repo/ui/full-calendar'
import {
  CalendarThreeIcon,
  CalendarTwoIcon,
  DeleteIcon,
  GoalIcon,
  PlusIcon,
  TravelTwoIcon
} from '@repo/ui/icons'
import InformationBox from '@repo/ui/information-box'
import InformationToggle from '@repo/ui/information-toggle'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import MonthlyCalendar from '@repo/ui/monthly-calendar'
import MultiSelectInput from '@repo/ui/multiselect-input'
import SelectInput from '@repo/ui/select-input'
import Typography from '@repo/ui/typography'
import YearlyCalendar from '@repo/ui/yearly-calendar'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import { FullCalendarEvent } from '../../../../../packages/ui/components/molecules/full-calendar/types'
import { getCountries } from '../../api/countries/get-countries'
import { getFormulas } from '../../api/formula/get-formulas'
import { getPeriods } from '../../api/periods/get-periods'
import { addStaffAvailability } from '../../api/staff/add-staff-availability'
import { deleteStaffAvailability } from '../../api/staff/delete-staff-availability'
import { getStaff } from '../../api/staff/get-staff'
import { getStaffAffectations } from '../../api/staff/get-staff-affectations'
import { getStaffAvailabilities } from '../../api/staff/get-staff-availabilities'
import { getStaffMissions } from '../../api/staff/get-staff-missions'
import { updateStaff } from '../../api/staff/staff-update'
import { updateStaffAvailability } from '../../api/staff/update-staff-availability'
import LoadingScreen from '../../components/elements/loading/loading-screen'
import UserNavBar from '../../components/elements/nav-bar'
import { StaffAvailabilityForm } from '../../components/pages/staffs/staff-availability-form'
import useAuth from '../../lib/hooks/use-auth'
import { useModalManager } from '../../lib/hooks/use-modal-manager'
import useTranslate from '../../lib/hooks/use-translate'
import { MissionStaffStatusEnum } from '../../lib/types/enums/mission/mission-staff-status.enum'
import { StaffPositionEnum } from '../../lib/types/enums/staff/staff-position.enum'
import { StaffAffectation } from '../../lib/types/models/affectedStaff/staff-affectation'
import { Availability } from '../../lib/types/models/availability/availability'
import { Country } from '../../lib/types/models/country/country'
import { Formula } from '../../lib/types/models/formula/formula'
import { MissionToStaffMission } from '../../lib/types/models/mission-to-staff/mission-to-staff'
import { showAxiosError } from '../../lib/utils/functions/misc.functions'
import { getTranslatedName } from '../../lib/utils/functions/translation.functions'
import { getPeriodsDates } from './utils'

const InformationData = ({ position }: { position?: StaffPositionEnum }) => {
  const { t } = useTranslate()

  // AAT staff can register an availability of a single day, so the minimum
  // duration is not mentioned to them
  const isAat = position === StaffPositionEnum.AAT

  return (
    <div className="flex flex-col gap-3">
      <Typography variant="secondary-body" color={tailwindColors.grey.DEFAULT}>
        {t('staff.personal.space.availability.first.information.part.one')}&nbsp;
        <span className="font-semibold text-sm">
          {t(
            isAat
              ? 'staff.personal.space.availability.first.information.part.two.aat'
              : 'staff.personal.space.availability.first.information.part.two'
          )}
        </span>
        &nbsp; {t('staff.personal.space.availability.first.information.part.three')}
      </Typography>
      <Typography variant="secondary-body" color={tailwindColors.grey.DEFAULT}>
        {t('staff.personal.space.availability.second.information.part.one')}&nbsp;
        <span className="font-semibold text-sm">
          {t('staff.personal.space.availability.second.information.part.two')}
        </span>
        &nbsp; {t('staff.personal.space.availability.second.information.part.three')}
      </Typography>
    </div>
  )
}

// "Disponible du 04/11/2026 au 06/11/2026", shared by the pill and its tooltip
const getAvailabilityLabel = (
  event: Pick<FullCalendarEvent, 'startDate' | 'endDate'>,
  t: ReturnType<typeof useTranslate>['t']
) =>
  `${t('staff.personal.space.availability.Avaialable.from')} ${DateFormatter.DDMMYYYY(event.startDate)} ${t('staff.personal.space.availability.to')} ${DateFormatter.DDMMYYYY(event.endDate)}`

const AvailabilitySlot = (event: FullCalendarEvent) => {
  const { t } = useTranslate()

  return (
    <div className="flex items-center space-x-2 min-w-0">
      <div className="flex items-center justify-center h-8 w-8 rounded-full bg-primary shrink-0">
        <CalendarThreeIcon width="30px" height="30px" color={tailwindColors.white.DEFAULT} />
      </div>
      <Typography variant="secondary-body" className="truncate">
        {getAvailabilityLabel(event, t)}
      </Typography>
    </div>
  )
}

AvailabilitySlot.style = {
  borderStyle: 'dashed',
  dayColor: tailwindColors.black.DEFAULT,
  backgroundColor: tailwindColors.primary.light.DEFAULT,
  borderColor: tailwindColors.primary.DEFAULT
}

const JourneySlot = (event: FullCalendarEvent) => {
  return (
    <div className="flex items-center space-x-2 min-w-0">
      <div className="flex items-center justify-center h-8 w-8 rounded-full bg-red-light shrink-0">
        <TravelTwoIcon width="30px" height="30px" color={tailwindColors.secondaryRed.DEFAULT} />
      </div>
      <Typography variant="secondary-body" className="truncate">
        {event.details}
      </Typography>
    </div>
  )
}

JourneySlot.style = {
  borderStyle: 'solid',
  color: tailwindColors.secondaryRed.DEFAULT,
  dayColor: tailwindColors.secondaryRed.DEFAULT,
  backgroundColor: tailwindColors.secondaryRed.light.DEFAULT,
  borderColor: tailwindColors.secondaryRed.light.active
}

const MissionSlot = (event: FullCalendarEvent) => {
  return (
    <div className="flex items-center space-x-2 min-w-0">
      <div className="flex items-center justify-center h-8 w-8 rounded-full bg-green-light shrink-0">
        <GoalIcon width="30px" height="30px" color={tailwindColors.green.DEFAULT} />
      </div>
      <Typography variant="secondary-body" className="truncate">
        {event.details}
      </Typography>
    </div>
  )
}

MissionSlot.style = {
  borderStyle: 'solid',
  color: tailwindColors.green.DEFAULT,
  dayColor: tailwindColors.green.DEFAULT,
  backgroundColor: tailwindColors.green.light.DEFAULT,
  borderColor: tailwindColors.green.light.active
}

const getEventStyle = (event: FullCalendarEvent) =>
  event.type === 'availability'
    ? AvailabilitySlot.style
    : event.type === 'mission'
      ? MissionSlot.style
      : JourneySlot.style

const StaffAvailabilityPage = () => {
  const { user } = useAuth()
  const { modalState, selectedData, toggleModal, closeModel } = useModalManager<
    'add-availability' | 'update-availability' | 'delete-availability',
    Partial<Availability>
  >(['add-availability', 'update-availability', 'delete-availability'])

  const device = useBreakPoint()
  const [viewMode, setViewMode] = useState<'monthly' | 'yearly'>('monthly')
  const [countriesWishList, setCountriesWishList] = useState<Country[]>([])
  const [formulasWishList, setFormulasWishList] = useState<Formula[]>([])
  const [month, setMonth] = useState(new Date().getMonth())
  const [year, setYear] = useState(new Date().getFullYear())

  const [events, setEvents] = useState<FullCalendarEvent[]>([])
  const { t, currentLanguage } = useTranslate()

  const staffId = user?.staff?.id

  if (!staffId) return null

  const { data: staff } = useQuery({
    queryKey: ['staff', staffId, 'FULL'],
    queryFn: () => getStaff(staffId)
  })

  const { data: availabilities } = useQuery({
    queryKey: ['staff-availabilities', staffId],
    queryFn: () => getStaffAvailabilities(staffId)
  })

  const { data: affectations } = useQuery({
    queryKey: ['staff-affectations', staffId],
    queryFn: () => getStaffAffectations(staffId)
  })

  // Cancelled affectations are not work the staff still has to do, so they stay off the calendar
  const { data: missions } = useQuery({
    queryKey: ['staff-missions', staffId],
    queryFn: () =>
      getStaffMissions(staffId, {
        filter: {
          statuses: [
            MissionStaffStatusEnum.PRE_AFFECTED,
            MissionStaffStatusEnum.AFFECTED,
            MissionStaffStatusEnum.PAID
          ]
        }
      })
  })

  const { mutateAsync: addStaffAvailabilityMutation, isPending: isLoadingAdd } = useMutation({
    mutationFn: addStaffAvailability,
    onError: showAxiosError
  })

  const { mutateAsync: updateStaffAvailabilityMutation, isPending: isLoadingUpdate } = useMutation(
    {
      mutationFn: updateStaffAvailability,
      onError: showAxiosError
    }
  )

  const { mutateAsync: deleteStaffAvailabilityMutation } = useMutation({
    mutationFn: deleteStaffAvailability,
    onError: showAxiosError
  })

  const { mutateAsync: updateStaffMutation } = useMutation({
    mutationFn: updateStaff,
    onError: showAxiosError
  })

  const { data: countries } = useQuery({
    queryKey: ['countries'],
    queryFn: () => getCountries()
  })

  const { data: formulas } = useQuery({
    queryKey: ['formulas'],
    queryFn: () => getFormulas()
  })

  const { data: periods } = useQuery({
    queryKey: ['periods', 'LIGHT'],
    queryFn: () => getPeriods({ version: 'LIGHT' })
  })

  const mapAvailabilityToEvent = (availability: Availability): FullCalendarEvent => ({
    id: 'availability-' + availability.id,
    endDate: DateFormatter.toDate(availability.endDate),
    startDate: DateFormatter.toDate(availability.startDate),
    title: 'Disponibilité',
    type: 'availability',
    onUpdate: async (oldEvent, newEvent) => {
      try {
        const availabilityId = oldEvent.id.replace('availability-', '')
        const result = await updateStaffAvailabilityMutation({
          id: staffId,
          availabilityId: parseInt(availabilityId),
          startDate: DateFormatter.toDateFormatted(newEvent.startDate),
          endDate: DateFormatter.toDateFormatted(newEvent.endDate)
        })
        return !!result
      } catch (error) {
        return false
      }
    }
  })

  // "Contrat | du 13/08/2026 au 27/08/2026 | Angleterre | Formule"
  const mapAffectationToEvent = (affectation: StaffAffectation): FullCalendarEvent => ({
    id: 'journey-' + affectation.id,
    type: 'journey',
    endDate: DateFormatter.toDate(affectation.endDate),
    startDate: DateFormatter.toDate(affectation.startDate),
    title: t('staff.personal.space.availability.contract'),
    details: [
      t('staff.personal.space.availability.contract'),
      `${t('staff.personal.space.availability.contract.from')} ${DateFormatter.DDMMYYYY(affectation.startDate)} ${t('staff.personal.space.availability.to')} ${DateFormatter.DDMMYYYY(affectation.endDate)}`,
      affectation.country?.name,
      affectation.formulas.map((formula) => getTranslatedName(formula)).join(', ')
    ]
      .filter(Boolean)
      .join(' | ')
  })

  // A mission is a single travel day
  const mapMissionToEvent = (missionToStaff: MissionToStaffMission): FullCalendarEvent => ({
    id: 'mission-' + missionToStaff.id,
    type: 'mission',
    endDate: new Date(missionToStaff.mission.date),
    startDate: new Date(missionToStaff.mission.date),
    title: t('staff.personal.space.availability.mission'),
    details: `${t('staff.personal.space.availability.mission')} | ${DateFormatter.DDMMYYYY(missionToStaff.mission.date)} | ${missionToStaff.mission.title}`
  })

  const allowedDates = getPeriodsDates(periods?.data || [])

  useEffect(() => {
    if (availabilities && affectations && missions) {
      const newEvents = availabilities.map(mapAvailabilityToEvent)
      const affectationEvents = affectations.map(mapAffectationToEvent)
      const missionEvents = missions.data.map(mapMissionToEvent)
      setEvents([...newEvents, ...affectationEvents, ...missionEvents])
    }
  }, [availabilities, affectations, missions])

  useEffect(() => {
    if (staff) {
      setCountriesWishList(staff.countriesWishList)
      setFormulasWishList(staff.formulasWishList)
    }
  }, [staff])
  if (!staff || !countries || !formulas) return <LoadingScreen />

  return (
    <div className="flex flex-col gap-5">
      <UserNavBar title={t('staff.personal.space.availability')} icon={CalendarThreeIcon} />
      <Box className="px-5 py-6 relative">
        <InformationToggle>
          <InformationData position={staff.position} />
        </InformationToggle>
        <Typography variant="sub-heading">
          {t('staff.personal.space.availability.title')}
        </Typography>
        <div className="h-5" />
        <InformationBox>
          <InformationData position={staff.position} />
        </InformationBox>

        <div className="h-5" />
        <div className="flex items-center justify-between gap-4 mobile:flex-col">
          <MultiSelectInput
            searchable
            selectedOptions={
              countriesWishList?.map((country) => ({
                label: country.name,
                value: country.id
              })) ?? []
            }
            variant="primary"
            dropDownPosition="above"
            onChange={(values) => {
              const selectedCountries = countries.data.filter((country) =>
                values.includes(country.id)
              )
              updateStaffMutation({
                id: staffId,
                countriesWishListIds: selectedCountries.map((country) => country.id)
              })
              setCountriesWishList(selectedCountries)
            }}
            options={
              countries?.data.map((country) => ({ label: country.name, value: country.id })) || []
            }
            label={t('staff.personal.space.availability.list.of.countries')}
          />
          <MultiSelectInput
            selectedOptions={
              formulasWishList?.map((formula) => ({
                label: getTranslatedName(formula),
                value: formula.id
              })) ?? []
            }
            variant="primary"
            onChange={(values) => {
              const selectedFormulas = formulas.data.filter((formula) =>
                values.includes(formula.id)
              )
              updateStaffMutation({
                id: staffId,
                formulasWishListIds: selectedFormulas.map((formula) => formula.id)
              })
              setFormulasWishList(selectedFormulas)
            }}
            options={
              formulas?.data.map((formula) => ({
                label: getTranslatedName(formula),
                component: (
                  <Typography variant="label-medium">
                    {getTranslatedName(formula) + ' '}
                    <span className="font-normal text-xs">({formula.description})</span>
                  </Typography>
                ),
                value: formula.id
              })) || []
            }
            label={t('staff.personal.space.availability.formula.wishlist')}
          />
          <Button
            variant="primary"
            className="w-full mobile:flex hidden"
            label={t('staff.personal.space.availability.add.availability')}
            icon={PlusIcon}
            onClick={() => {
              toggleModal('add-availability')
            }}
          />

          <div className="mobile:grid mobile:grid-cols-2 items-center gap-2 hidden w-full ">
            <SelectInput
              label={t('staff.personal.space.availability.month')}
              options={months[currentLanguage].map((month, index) => ({
                label: month,
                value: index
              }))}
              selectedOption={{ label: months[currentLanguage][month], value: month }}
              onChange={(value) => setMonth(value)}
            />
            <SelectInput
              label={t('staff.personal.space.availability.year')}
              options={years.map((year) => ({ label: year.toString(), value: year }))}
              selectedOption={{ label: year.toString(), value: year }}
              onChange={(value) => setYear(value)}
            />
          </div>
        </div>
        <div className="h-5" />
        {device === 'mobile' ? (
          <MonthlyCalendar
            allowedDates={allowedDates}
            year={year}
            month={month}
            events={events.map((event) => ({
              ...event,
              onClick: () => {
                if (event.type !== 'availability') return

                const id = event.id.replace('availability-', '').replace('journey-', '')
                toggleModal('update-availability', {
                  ...event,
                  id: parseInt(id),
                  startDate: DateFormatter.toDateFormatted(event.startDate),
                  endDate: DateFormatter.toDateFormatted(event.endDate)
                })
              },
              style: getEventStyle(event)
            }))}
          />
        ) : viewMode === 'monthly' ? (
          <FullCalendar
            allowedDates={allowedDates}
            onDaySelected={(date) => {
              toggleModal('add-availability', {
                startDate: DateFormatter.toDateFormatted(date)
              })
            }}
            events={events.map((event) => ({
              ...event,
              onClick: () => {
                if (event.type !== 'availability') return

                const id = event.id.replace('availability-', '')
                toggleModal('update-availability', {
                  ...event,
                  id: parseInt(id),
                  startDate: DateFormatter.toDateFormatted(event.startDate),
                  endDate: DateFormatter.toDateFormatted(event.endDate)
                })
              },
              title:
                event.type === 'availability' ? (
                  <AvailabilitySlot {...event} />
                ) : event.type === 'mission' ? (
                  <MissionSlot {...event} />
                ) : (
                  <JourneySlot {...event} />
                ),
              tooltip:
                event.type === 'availability'
                  ? getAvailabilityLabel(event, t)
                  : typeof event.details === 'string'
                    ? event.details
                    : undefined,
              disableDragging: event.type !== 'availability',
              disableExtend: event.type !== 'availability',
              style: getEventStyle(event)
            }))}
            onEventsChanged={setEvents}
            headerActions={[
              <Button
                key={1}
                variant="primary"
                label={t('staff.personal.space.availability.add.availability')}
                icon={PlusIcon}
                onClick={() => {
                  toggleModal('add-availability')
                }}
              />,
              <Button
                key={2}
                variant="secondary"
                label={t('staff.personal.space.availability.month')}
                icon={CalendarTwoIcon}
                onClick={() => {
                  setViewMode('yearly')
                }}
              />
            ]}
          />
        ) : (
          <YearlyCalendar
            allowedDates={allowedDates}
            events={events.map((event) => ({
              ...event,
              onClick: () => {
                if (event.type !== 'availability') return

                const id = event.id.replace('availability-', '').replace('journey-', '')
                toggleModal('update-availability', {
                  ...event,
                  id: parseInt(id),
                  startDate: DateFormatter.toDateFormatted(event.startDate),
                  endDate: DateFormatter.toDateFormatted(event.endDate)
                })
              },
              style: getEventStyle(event)
            }))}
            headerActions={[
              <Button
                key={1}
                variant="primary"
                label={t('staff.personal.space.availability.add.availability')}
                icon={PlusIcon}
                onClick={() => {
                  toggleModal('add-availability')
                }}
              />,
              <Button
                key={2}
                variant="secondary"
                label={t('staff.personal.space.availability.year')}
                icon={CalendarTwoIcon}
                onClick={() => {
                  setViewMode('monthly')
                }}
              />
            ]}
          />
        )}

        <div className="h-4" />
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <div
              className="w-12 h-5 bg-secondary-light border border-solid rounded"
              style={{ borderColor: tailwindColors.secondaryRed.light.active }}
            />
            <Typography variant="tag">
              {t('staff.personal.space.availability.contract')}
            </Typography>
          </div>
          <div className="flex items-center gap-2">
            <div
              className="w-12 h-5 bg-green-light border border-solid rounded"
              style={{ borderColor: tailwindColors.green.light.active }}
            />
            <Typography variant="tag">{t('staff.personal.space.availability.mission')}</Typography>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-12 h-5 bg-primary-light border border-dashed border-primary rounded" />
            <Typography variant="tag">{t('staff.personal.space.availability')}</Typography>
          </div>
        </div>
      </Box>

      {/* Create new availability */}
      <Modal
        isOpen={modalState['add-availability']}
        title={t('staff.personal.space.availability.new.availability')}
        hideActions
        icon={(props) => <CalendarThreeIcon {...props} color={tailwindColors.secondary.DEFAULT} />}
        onCancel={() => toggleModal('add-availability')}
      >
        <StaffAvailabilityForm
          className={MODAL_BODY_CLASSNAME}
          handleSubmit={async (data) => {
            const availability = await addStaffAvailabilityMutation({
              id: staffId,
              startDate: data.startDate,
              endDate: data.endDate
            })
            setEvents((prevEvents) => [...prevEvents, mapAvailabilityToEvent(availability)])
            toggleModal('add-availability')
          }}
          onCancel={() => toggleModal('add-availability')}
          submitText={t('button.add')}
          cancelText={t('button.cancel')}
          defaultValues={{ startDate: selectedData?.startDate }}
          isLoading={isLoadingAdd}
          position={staff.position}
        />
      </Modal>

      {/* Update Availability */}
      {selectedData?.startDate && selectedData?.endDate && (
        <Modal
          isOpen={modalState['update-availability']}
          title={t('staff.personal.space.availability.edit.availability')}
          hideActions
          icon={(props) => (
            <CalendarThreeIcon {...props} color={tailwindColors.secondary.DEFAULT} />
          )}
          subTitle={`${DateFormatter.DDMMYYYY(selectedData.startDate)} - ${DateFormatter.DDMMYYYY(selectedData.endDate)}`}
          onCancel={() => toggleModal('update-availability')}
        >
          <StaffAvailabilityForm
            className={`px-5`}
            onDelete={() => {
              if (!selectedData.id) return
              closeModel('update-availability')
              toggleModal('delete-availability', selectedData)
            }}
            handleSubmit={async (data) => {
              if (!selectedData.id) return
              await updateStaffAvailabilityMutation({
                id: staffId,
                availabilityId: selectedData.id,
                startDate: data.startDate,
                endDate: data.endDate
              })
              // add the new event to the calendar
              setEvents((prevEvents) => {
                const updatedEvents = prevEvents.map((event) => {
                  if (event.id === 'availability-' + selectedData.id)
                    return { ...event, startDate: data.startDate, endDate: data.endDate }
                  return {
                    ...event,
                    startDate: DateFormatter.toDateFormatted(event.startDate),
                    endDate: DateFormatter.toDateFormatted(event.endDate)
                  }
                })
                return updatedEvents.map((event) => ({
                  ...event,
                  startDate: DateFormatter.toDate(event.startDate),
                  endDate: DateFormatter.toDate(event.endDate)
                }))
              })
              toggleModal('update-availability')
            }}
            cancelText={t('button.cancel')}
            submitText={t('button.validate')}
            onCancel={() => toggleModal('update-availability')}
            defaultValues={{ startDate: selectedData.startDate, endDate: selectedData.endDate }}
            isLoading={isLoadingUpdate}
            position={staff.position}
          />
        </Modal>
      )}

      {selectedData?.startDate && selectedData?.endDate && (
        <Modal
          actionButtonsOptions={{
            confirmText: t('button.delete'),
            cancelText: t('button.cancel'),
            onConfirm: async () => {
              if (!selectedData.id) return
              await deleteStaffAvailabilityMutation({
                id: staffId,
                availabilityId: selectedData.id
              })
              // remove the event from the calendar
              setEvents((prevEvents) =>
                prevEvents.filter((event) => event.id !== 'availability-' + selectedData.id)
              )
              toggleModal('delete-availability')
            }
          }}
          onCancel={() => toggleModal('delete-availability')}
          isOpen={modalState['delete-availability']}
          title={t('staff.personal.space.availability.form.delete.message')}
          icon={DeleteIcon}
          variant="destructive"
        />
      )}
    </div>
  )
}

export default StaffAvailabilityPage
